import { Navigate, Outlet } from 'react-router-dom'
import { t } from 'i18next'
import { Button } from '@asad/ui'

import { useAppDispatch, useAppSelector } from '@/hooks/reduxHooks'
import { LoadingState } from '@asad/crane-ui'
import { readAuthFlag, readUser } from '@app/store/services/local-storage.service'
import { getProfileData } from '@app/store/slices/profile-slice'

type ProtectedRouteProps = {
  requiredRoles?: string[]
}

/** Jedyne miejsce, które odpowiada na pytanie „gdzie należy ten użytkownik".
 *
 *  Kolejność ma znaczenie wyłącznie dla kont z kilkoma rolami — wygrywa pierwsza
 *  pasująca. Wcześniej ta sama wyliczanka stała w dwóch plikach w RÓŻNEJ kolejności,
 *  więc konto z rolami RODO i dziennika zdarzeń lądowało gdzie indziej po zalogowaniu,
 *  a gdzie indziej po odbiciu od zabronionej trasy. Zachowana jest kolejność z ekranu
 *  startowego, bo to ona jest pierwszym, co widzi użytkownik. */
const ROLE_LANDING: ReadonlyArray<readonly [role: string, path: string]> = [
  ['ADMIN', '/dashboard'],
  ['ADMIN_RODO', '/rodo/users-consents'],
  ['ADMIN_AUDIT_LOG', '/audit-log'],
  ['SERVICE_MANAGER', '/service'],
  ['SERVICE_TECHNICIAN', '/my-orders'],
]

export const getFallbackPath = (roles: string[]): string =>
  ROLE_LANDING.find(([role]) => roles.includes(role))?.[1] ?? '/dashboard'

const ProtectedRoute = ({ requiredRoles = [] }: ProtectedRouteProps) => {
  const dispatch = useAppDispatch()
  const { data: profile, status } = useAppSelector((state) => state.profile)

  if (!readAuthFlag()) {
    return <Navigate to='/auth/login' replace />
  }

  // Loader tylko dopóki nie ma czego renderować. Odświeżenie profilu w tle (np. po zapisie
  // urządzenia przypisanego do zalogowanego użytkownika) też ustawia status 'loading' —
  // podmiana Outletu na loader odmontowałaby wtedy cały widok razem z otwartym modalem
  // formularza i zamontowała go ponownie po odpowiedzi.
  const isProfilePending = !profile && (status === 'loading' || status === 'idle')

  if (isProfilePending) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <LoadingState />
      </div>
    )
  }

  if (!profile) {
    const userId = readUser()

    return (
      <div className='flex flex-col items-center justify-center gap-6 py-24'>
        <p className='text-lg'>{t('common.connectionError')}</p>
        <Button
          variant='primary'
          onClick={() => {
            if (userId) dispatch(getProfileData(userId))
          }}
        >
          {t('common.retry')}
        </Button>
      </div>
    )
  }

  const userRoles: string[] = Array.isArray(profile.role) ? profile.role : profile.role ? [profile.role] : []
  const hasRequiredRole =
    requiredRoles.length === 0 || requiredRoles.some((requiredRole) => userRoles.includes(requiredRole))

  if (!hasRequiredRole) {
    return <Navigate to={getFallbackPath(userRoles)} replace />
  }

  return <Outlet />
}

export default ProtectedRoute
